import { useEffect, useState } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  CANONICAL_COMPLIANCE_REVIEW_REASONS,
} from "@/components/compliance-review/canonicalComplianceReviewUi";
import {
  CANONICAL_INTERNAL_REVIEW_READ_ERROR_MESSAGES,
  CanonicalInternalReviewReadClientError,
} from "@/services/canonical-internal-review-read.shared";

export type CanonicalReviewDecision =
  | "approved"
  | "rejected"
  | "changes_requested";

function createIdempotencyKey(): string {
  if (
    typeof crypto !== "undefined"
    && typeof crypto.randomUUID === "function"
  ) return crypto.randomUUID();
  if (
    typeof crypto !== "undefined"
    && typeof crypto.getRandomValues === "function"
  ) {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = [...bytes].map((value) => value.toString(16).padStart(2, "0"));
    return [
      hex.slice(0, 4).join(""),
      hex.slice(4, 6).join(""),
      hex.slice(6, 8).join(""),
      hex.slice(8, 10).join(""),
      hex.slice(10).join(""),
    ].join("-");
  }
  throw new Error("secure_idempotency_unavailable");
}

function decisionLabel(decision: CanonicalReviewDecision): string {
  if (decision === "approved") return "Approve";
  if (decision === "rejected") return "Reject";
  return "Request Changes";
}

export function CanonicalReviewDecisionDialog({
  decision,
  targetLabel,
  onClose,
  onConfirm,
}: Readonly<{
  decision: CanonicalReviewDecision | null;
  targetLabel: string;
  onClose: () => void;
  onConfirm: (input: Readonly<{
    reasonCode?: string;
    note?: string;
    idempotencyKey: string;
  }>) => Promise<void>;
}>) {
  const [reasonCode, setReasonCode] = useState("");
  const [note, setNote] = useState("");
  const [attemptKey, setAttemptKey] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setReasonCode("");
    setNote("");
    setAttemptKey(null);
    setSubmitting(false);
    setError("");
  }, [decision, targetLabel]);

  async function submit(): Promise<void> {
    if (!decision || submitting) return;
    if (decision !== "approved" && !reasonCode) {
      setError("Select a reason before confirming this decision.");
      return;
    }
    if (reasonCode === "other" && !note.trim()) {
      setError("Add a note when the selected reason is Other.");
      return;
    }
    const key = attemptKey ?? createIdempotencyKey();
    setAttemptKey(key);
    setSubmitting(true);
    setError("");
    try {
      await onConfirm({
        ...(decision === "approved" ? {} : { reasonCode }),
        ...(note.trim() ? { note: note.trim() } : {}),
        idempotencyKey: key,
      });
    } catch (caught) {
      const kind = caught instanceof CanonicalInternalReviewReadClientError
        ? caught.kind
        : "unexpected_error";
      setError(CANONICAL_INTERNAL_REVIEW_READ_ERROR_MESSAGES[kind].description);
      if (kind === "stale_decision" || kind === "idempotency_conflict") {
        setAttemptKey(null);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={decision !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {decision ? decisionLabel(decision) : "Review decision"}
          </DialogTitle>
          <DialogDescription>
            Confirm an append-only decision for {targetLabel}.
          </DialogDescription>
        </DialogHeader>

        {decision && decision !== "approved" ? (
          <div className="space-y-1">
            <Label htmlFor="canonical-domain-review-reason">Reason</Label>
            <select
              id="canonical-domain-review-reason"
              className="h-10 w-full rounded-md border bg-background px-3 text-sm"
              value={reasonCode}
              onChange={(event) => setReasonCode(event.currentTarget.value)}
            >
              <option value="">Select reason</option>
              {CANONICAL_COMPLIANCE_REVIEW_REASONS.map((reason) => (
                <option key={reason.code} value={reason.code}>
                  {reason.label}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div className="space-y-1">
          <Label htmlFor="canonical-domain-review-note">
            Reviewer note
            {reasonCode === "other" ? " (required)" : " (optional)"}
          </Label>
          <Textarea
            id="canonical-domain-review-note"
            value={note}
            maxLength={2000}
            rows={4}
            onChange={(event) => setNote(event.currentTarget.value)}
            placeholder="Plain text only. Do not include URLs or credentials."
          />
        </div>

        {error ? (
          <Alert variant="destructive" aria-live="assertive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Decision not submitted</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        <DialogFooter>
          <Button variant="outline" disabled={submitting} onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={submitting} onClick={() => void submit()}>
            {submitting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            Confirm decision
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
